import React, { useState } from "react";
import { Icon } from "@iconify/react";
import PostHooks from "../../Hooks/PostHooks";
import { server_url } from "../../Config/API";
import { singleImageUpload } from "../../Hooks/ImageUpload";
import { toast } from "react-toastify";

function SuperAdminProductAdd() {
  const [imageUrl, setImageUrl] = useState("");
  const [productData, setProductData] = useState({
    name: "",
    description: "",
    price: 0,
    stock: 0,
    categories: "",
    weight: 0,
    dimensions: "",
    isFeatured: false,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProductData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!imageUrl) {
      toast.error("Please select a product image");
      return;
    }
    await PostHooks(
      `${server_url}/products`,
      { ...productData, imageUrl },
      "Product added successfully!"
    );
  };

  const handleChangeUploadImage = async (event) => {
    const image = event.target.files[0];
    const formData = new FormData();
    formData.append("image", image);
    singleImageUpload(formData, setImageUrl);
  };

  return (
    <div className="max-w-4xl mx-auto select-none space-y-6">
      {/* Header card */}
      <div className="flex items-center gap-3 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
          <Icon icon="solar:box-bold" className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-lg font-black text-slate-800 tracking-tight">Upload New Product</h1>
          <p className="text-xs text-slate-400 font-semibold mt-0.5">Add a new medical product to the platform store</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden p-6 sm:p-8 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {/* Product Name */}
          <div className="space-y-1.5">
            <label htmlFor="productName" className="block text-xs font-bold text-slate-500 uppercase tracking-wide">
              Product Name
            </label>
            <div className="relative">
              <input
                id="productName"
                name="name"
                required
                type="text"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm placeholder-slate-400 transition-all font-medium"
                value={productData.name}
                onChange={handleChange}
                placeholder="e.g. Disposable Syringe"
              />
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                <Icon icon="ri:product-hunt-line" className="w-5 h-5" />
              </div>
            </div>
          </div>

          {/* Price */}
          <div className="space-y-1.5">
            <label htmlFor="productPrice" className="block text-xs font-bold text-slate-500 uppercase tracking-wide">
              Price ($)
            </label>
            <div className="relative">
              <input
                id="productPrice"
                name="price"
                type="number"
                required
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm placeholder-slate-400 transition-all font-medium"
                value={productData.price}
                onChange={handleChange}
                placeholder="Product Price"
              />
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                <Icon icon="ri:price-tag-line" className="w-5 h-5" />
              </div>
            </div>
          </div>

          {/* Stock */}
          <div className="space-y-1.5">
            <label htmlFor="productStock" className="block text-xs font-bold text-slate-500 uppercase tracking-wide">
              Stock Quantity
            </label>
            <div className="relative">
              <input
                id="productStock"
                name="stock"
                required
                type="number"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm placeholder-slate-400 transition-all font-medium"
                value={productData.stock}
                onChange={handleChange}
                placeholder="Product Stock"
              />
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                <Icon icon="ant-design:stock-outlined" className="w-5 h-5" />
              </div>
            </div>
          </div>

          {/* Product Image */}
          <div className="space-y-1.5">
            <label htmlFor="productImage" className="block text-xs font-bold text-slate-500 uppercase tracking-wide">
              Product Image
            </label>
            <div className="relative">
              <input
                type="file"
                required={!imageUrl}
                id="productImage"
                name="imageUrl"
                className="w-full pl-10 pr-4 py-2 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm file:mr-4 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-[10px] file:font-extrabold file:uppercase file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 transition-all cursor-pointer"
                onChange={handleChangeUploadImage}
                accept="image/*"
              />
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                <Icon icon="material-symbols:image-outline" className="w-5 h-5" />
              </div>
            </div>
          </div>

          {/* Categories */}
          <div className="space-y-1.5">
            <label htmlFor="productCategories" className="block text-xs font-bold text-slate-500 uppercase tracking-wide">
              Categories
            </label>
            <div className="relative">
              <input
                id="productCategories"
                name="categories"
                type="text"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm placeholder-slate-400 transition-all font-medium"
                value={productData.categories}
                onChange={handleChange}
                placeholder="e.g. Syringes, Gloves"
              />
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                <Icon icon="carbon:expand-categories" className="w-5 h-5" />
              </div>
            </div>
          </div>

          {/* Weight */}
          <div className="space-y-1.5">
            <label htmlFor="productWeight" className="block text-xs font-bold text-slate-500 uppercase tracking-wide">
              Weight (kg)
            </label>
            <div className="relative">
              <input
                id="productWeight"
                name="weight"
                type="number"
                step="any"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm placeholder-slate-400 transition-all font-medium"
                value={productData.weight}
                onChange={handleChange}
                placeholder="Product Weight"
              />
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                <Icon icon="fa6-solid:weight-scale" className="w-4 h-4" />
              </div>
            </div>
          </div>

          {/* Dimensions */}
          <div className="space-y-1.5 sm:col-span-2">
            <label htmlFor="productDimensions" className="block text-xs font-bold text-slate-500 uppercase tracking-wide">
              Dimensions (L x W x H)
            </label>
            <div className="relative">
              <input
                id="productDimensions"
                name="dimensions"
                type="text"
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm placeholder-slate-400 transition-all font-medium"
                value={productData.dimensions}
                onChange={handleChange}
                placeholder="e.g. 10 x 5 x 2 cm"
              />
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                <Icon icon="tabler:dimensions" className="w-5 h-5" />
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="space-y-1.5 sm:col-span-2">
            <label htmlFor="productDescription" className="block text-xs font-bold text-slate-500 uppercase tracking-wide">
              Description
            </label>
            <textarea
              id="productDescription"
              name="description"
              rows="4"
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 text-sm placeholder-slate-400 transition-all font-medium"
              value={productData.description}
              onChange={handleChange}
              placeholder="Enter product details..."
            />
          </div>
        </div>

        {/* Submit */}
        <div className="pt-4 border-t border-slate-50 flex justify-end">
          <button
            type="submit"
            className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 hover:opacity-95 text-white font-extrabold text-xs rounded-xl shadow-md shadow-cyan-500/10 hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
          >
            <Icon icon="zondicons:add-outline" className="w-4 h-4" />
            <span>Add Product</span>
          </button>
        </div>
      </form>
    </div>
  );
}

export default SuperAdminProductAdd;
